import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OnCraftAction, useImageCropper } from '@go-client/context/ImageCropperContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { lastEl, notUndefined } from '@go-shared/lib/functional';
import {
  getMissingImageRequirements,
  getOptimizedImageUrl,
  THUMBNAIL_OPTIMIZATION,
} from '@go-shared/lib/image';
import { copyToClipboard } from '@go-shared/lib/utils';
import { ImageBase, ImageVariantBase } from '@go-shared/types/image';
import { PartialBy } from '@go-shared/types/typeUtils';
import React from 'react';
import { Bytes } from '../Image/Bytes';
import BubbleBadge from './BubbleBadge';
import BubbleButton from './BubbleButton';
import { Tooltip } from '@spark-labs/avocado-ui';
import { resolveAssetsPath } from '@go-client/routes';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import BubbleLoader from './BubbleLoader';

export type BubbleThumbnail = PartialBy<ImageBase, 'size' | 'mimeType'> & {
  name?: string;
  variants?: ImageVariantBase[];
};

interface BubbleImageThumbnailPropsBase {
  purpose: BubbleImageThumbnailPurpose;
  variants?: ImageVariantBase[];
  thumbnail?: BubbleThumbnail;
  editDefaultKey?: string;
  editDefaultOptimization?: string;
  width?: number;
  height?: number;
  maxBytes?: number;
  onCraft?: OnCraftAction;
  onDeleteClick?: () => void;
  readOnly?: boolean;
  loading?: boolean;
}

export type BubbleImageThumbnailPurpose =
  | 'first'
  | 'main'
  | 'variant'
  | 'list'
  | 'library'
  | 'button'
  | 'plain';

type BubbleImageThumbnailProps = BubbleImageThumbnailPropsBase &
  React.HTMLAttributes<HTMLDivElement>;

export default function BubbleImageThumbnail(props: BubbleImageThumbnailProps) {
  const {
    thumbnail: image,
    width: propWidth,
    height: propHeight,
    maxBytes: propMaxBytes,
    onCraft,
    className: propClasses,
    purpose,
    onDeleteClick,
    editDefaultKey,
    editDefaultOptimization,
    variants,
    readOnly: locked,
    loading,
    ...rest
  } = props;

  const { craftImage } = useImageCropper();
  const { plugins } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const classes = ['relative hover-visible no-image', props.onClick ? 'cursor-pointer' : ''];

  if (propClasses) {
    classes.push(propClasses);
  }

  const missingImageRequirements = getMissingImageRequirements(plugins, image?.variants);

  const topButtons = [
    ['library', 'main'].includes(purpose) && image?.key ? (
      <Tooltip text="Copy Image URL" className="w-full">
        <BubbleButton
          key="copy"
          onClick={() => copyToClipboard(image.key!)}
          icon="copy"
          variant="white"
          inverse
          block
        />
      </Tooltip>
    ) : undefined,
    ['first', 'main', 'variant'].includes(purpose) && onCraft && !locked ? (
      <BubbleButton
        key="edit"
        variant="white"
        inverse
        block
        icon="edit"
        onClick={() =>
          craftImage(editDefaultKey ?? image?.key, purpose, editDefaultOptimization, onCraft)
        }
      />
    ) : undefined,
    purpose != 'button' && onDeleteClick && !locked ? (
      <BubbleButton
        key="delete"
        variant="white"
        inverse
        block
        icon="trash-alt"
        onClick={onDeleteClick}
      />
    ) : undefined,
    purpose === 'list' && image ? (
      <BubbleButton
        key="link"
        variant="white"
        inverse
        block
        icon="arrow-up-right"
        linkProps={{
          to: `${resolveAssetsPath(property._id, 'images')}?search=${image.key}`,
        }}
      />
    ) : undefined,
  ].filter(notUndefined);

  const metadata = [
    ['library', 'variant', 'main', 'first'].includes(purpose) && image?.size !== undefined ? (
      <BubbleBadge>
        <Bytes bytes={image.size} />
      </BubbleBadge>
    ) : undefined,
    ['library', 'variant', 'main', 'first'].includes(purpose) && image?.mimeType ? (
      <BubbleBadge>{lastEl(image.mimeType.split('/'))}</BubbleBadge>
    ) : undefined,
  ].filter(notUndefined);

  const title = ['library', 'list', 'button'].includes(purpose)
    ? image?.name || lastEl(image?.key.split('/') ?? []) || undefined
    : undefined;

  const showTachometer = ['library', 'list'].includes(purpose);
  const showBottom = title || metadata.length > 0;

  function getImageKey(img: BubbleThumbnail | undefined) {
    let key = img?.key;

    if (['library', 'button', 'list', 'plain'].includes(purpose)) {
      key = getOptimizedImageUrl(img, img?.variants, THUMBNAIL_OPTIMIZATION);
    }

    return key;
  }

  return (
    <div {...rest} className={classes.join(' ')}>
      <div
        className="aspect-square shadow"
        style={{
          backgroundImage: `url(${encodeURI(getImageKey(image) || '')})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {showTachometer && (
          <div
            className={`absolute top-0 right-0 mt-3 mr-3 grid items-center justify-center text-white rounded-full ${
              missingImageRequirements.length > 0 ? 'bg-banana' : 'bg-lime'
            }`}
            style={{ width: '1rem', height: '1rem' }}
          >
            <FontAwesomeIcon
              icon={
                missingImageRequirements.length > 0 ? 'tachometer-slowest' : 'tachometer-fastest'
              }
              fixedWidth
            />
          </div>
        )}
        {loading && (
          <div
            className="absolute top-0 left-0 w-full h-full grid grid-cols-1 items-center justify-center text-navy"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
          >
            <BubbleLoader width={35} height={35} className="mx-auto" style={{ fill: '#316e83' }} />
          </div>
        )}
        {topButtons.length > 0 && (
          <div className="hover-item absolute top-0 right-0 w-full bg-titanium-darkest text-white">
            <div className="flex items-center">{topButtons}</div>
          </div>
        )}
        {showBottom && (
          <div className="hover-item absolute bottom-0 left-0 w-full bg-titanium-darkest text-white px-3 py-2">
            {title && (
              <div
                className="font-semibold mb-1 overflow-hidden text-ellipsis"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title}
              </div>
            )}
            {metadata.length > 0 && <div className="flex items-center space-x-1">{metadata}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
